/* =============================================================================
   Formel setzen: Baum -> Kasten mit Zeichen und Strichen

   Ein Kasten hat eine Breite w, eine Höhe h über der Grundlinie und eine
   Tiefe t darunter, alles in Pixeln. Darin liegen
     glyphen  [{ ch, x, y, s }]   Zeichen der Schrift, y = Grundlinie (nach oben +)
     striche  [{ p: [x,y,…], d }] Polylinien (Bruchstrich, Wurzel, π …), d = Dicke

   Gesetzt wird so, wie man es auf Papier schreibt: Zähler und Nenner etwas
   kleiner, der Bruchstrich auf Höhe der Mitte von + und −, die Wurzel als ein
   Zug mit Haken, Hochzahlen klein und angehoben. Alle Abstände hängen an der
   x-Höhe der Schrift (u), damit es bei Anton genauso sitzt wie bei Moritz.
   ========================================================================== */

import { baueSymbol, glaette, UNICODE } from "./symbole";

const leer = () => ({ w: 0, h: 0, t: 0, glyphen: [], striche: [] });

function verschiebe(box, dx, dy) {
  return {
    w: box.w,
    h: Math.max(0, box.h + dy),
    t: Math.max(0, box.t - dy),
    glyphen: box.glyphen.map((g) => ({ ...g, x: g.x + dx, y: g.y + dy })),
    striche: box.striche.map((s) => ({
      ...s,
      p: s.p.map((v, i) => (i % 2 === 0 ? v + dx : v + dy)),
    })),
    tinte: box.tinte && {
      links: box.tinte.links + dx,
      rechts: box.tinte.rechts + dx,
      oben: box.tinte.oben + dy,
      unten: box.tinte.unten + dy,
    },
  };
}

/* Mehrere Kästen übereinander an derselben Stelle (für ≈ aus zwei ~). */
function vereine(kaesten) {
  const aus = leer();
  for (const b of kaesten) {
    aus.w = Math.max(aus.w, b.w);
    aus.h = Math.max(aus.h, b.h);
    aus.t = Math.max(aus.t, b.t);
    aus.glyphen.push(...b.glyphen);
    aus.striche.push(...b.striche);
  }
  return aus;
}

/* Höhe und Tiefe eines Kastens aus seinen Strichen nachziehen. */
function mitStrichen(box, striche) {
  let { h, t } = box;
  for (const s of striche) {
    for (let i = 1; i < s.p.length; i += 2) {
      h = Math.max(h, s.p[i] + s.d / 2);
      t = Math.max(t, -s.p[i] + s.d / 2);
    }
  }
  return { ...box, h, t, striche: [...box.striche, ...striche] };
}

function nebeneinander(teile) {
  const aus = leer();
  let x = 0;
  for (const { box, abstand = 0 } of teile) {
    x += abstand;
    const b = verschiebe(box, x, 0);
    aus.glyphen.push(...b.glyphen);
    aus.striche.push(...b.striche);
    aus.h = Math.max(aus.h, box.h);
    aus.t = Math.max(aus.t, box.t);
    x += box.w;
  }
  aus.w = x;
  return aus;
}

/* --- Setzer -------------------------------------------------------------------- */

export function setzeFormel(baum, umgebung) {
  const { metrik: M, S, laufweite, dicke, bruchzeile = false } = umgebung;

  /* Ein Zeichen der Schrift in Grösse k (1 = Grundgrösse). */
  const glyphBox = (ch, k) => {
    if (!M.hat(ch)) return null;
    const s = S * k;
    const m = M.glyph(ch);
    return {
      w: m.breite * s + laufweite(s),
      h: Math.max(0, m.oben * s),
      t: Math.max(0, m.unten * s),
      // versatz: Ersatzschrift auf die Grundlinie der Hauptschrift (metrik.js)
      glyphen: [{ ch, x: 0, y: -(m.versatz || 0) * s, s, ab: m.versatz || 0 }],
      striche: [],
      tinte: { links: m.links * s, rechts: m.rechts * s, oben: m.oben * s, unten: -m.unten * s },
    };
  };

  const strichDicke = (k) => dicke * Math.pow(Math.max(k, 0.45), 0.55);

  const hilfeFuer = (k) => {
    const u = M.xh * S * k;
    const d = strichDicke(k);
    return {
      u,
      d,
      k,
      A: M.achse / M.xh,
      C: M.ziffernHoehe / M.xh,
      hat: M.hat,
      glyphBox: (ch, f = 1) => glyphBox(ch, k * f),
      verschiebe,
      vereine,
      mitStrichen,
      mitRand: (box, rand) => {
        const r = rand * u;
        return { ...verschiebe(box, r, 0), w: box.w + 2 * r, h: box.h, t: box.t };
      },
      ausForm: (form) => {
        const rand = 0.13 * u;
        const striche = form.striche.map((s) => {
          let p = s.p.map((v, i) => (i % 2 === 0 ? v * u + rand : v * u));
          if (s.rund) p = glaette(p);
          return { p, d };
        });
        return mitStrichen({ w: form.w * u + 2 * rand, h: 0, t: 0, glyphen: [], striche: [] }, striche);
      },
    };
  };

  const symbol = (name, k) => {
    const zeichen = UNICODE[name];
    if (zeichen && M.hat(zeichen)) {
      const b = glyphBox(zeichen, k);
      if (b) return b;
    }
    return baueSymbol(name, hilfeFuer(k)) || leer();
  };

  /* Abstände um Rechen- und Vergleichszeichen, wie man sie von Hand lässt. */
  const luft = (kl, k) => {
    const u = M.xh * S * k;
    if (kl === "rel") return 0.62 * u;
    if (kl === "bin") return 0.42 * u;
    return 0;
  };

  function zeile(items, k) {
    const teile = [];
    let vorher = null; // Rolle des vorigen Elements
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      let kl = item.kl || "ord";
      // Ein Rechenzeichen ganz am Ende hat keinen Partner
      if (kl === "bin" && i === items.length - 1) kl = "ord";
      // Umformungsstrich „… = 7 | −3“: Luft wie um =, danach ist − ein Vorzeichen
      const davor = items[i - 1];
      if (item.t === "g" && item.ch === "|" && (!davor || davor.t === "abstand")) kl = "rel";
      // Vorzeichen: − am Anfang, nach = oder nach ( bekommt keine Luft
      if (kl === "bin" && (vorher === null || ["bin", "rel", "open", "punct"].includes(vorher))) kl = "ord";
      let box = setze(item, k);
      // In einer Rechnung mit Brüchen stehen + − = auf der Linie der Bruchstriche
      if (bruchzeile && k === 1 && (item.kl === "bin" || item.kl === "rel")) {
        box = verschiebe(box, 0, -M.achse * S);
      }
      let abstand = 0;
      if (vorher !== null) {
        abstand = Math.max(luft(vorher, k), luft(kl, k));
        if (vorher === "punct") abstand = 0.5 * M.xh * S * k;
        // Dezimalkomma 12,57 (bei ChatGPT 12{,}57): keine Lücke
        const ziffer = (x) => x && x.t === "g" && /[0-9]/.test(x.ch);
        if (vorher === "punct" && items[i - 1].ch === "," && ziffer(items[i - 2]) && ziffer(item)) abstand = 0;
        if (vorher === "op" && item.t !== "klammer") abstand = 0.3 * M.xh * S * k;
      }
      teile.push({ box, abstand });
      vorher = kl;
    }
    return nebeneinander(teile);
  }

  function bruch(knoten, k) {
    const kk = Math.max(0.55, k * (k > 0.9 ? 0.8 : 0.84));
    const oben = setze(knoten.oben, kk);
    const unten = setze(knoten.unten, kk);
    const u = M.xh * S * k;
    /* Bruch in normaler Grösse: Der Strich liegt genau auf der Papierlinie und
       deckt sie ab — Zähler darüber, Nenner darunter, wie von Hand auf
       liniertem oder kariertem Papier. Läge er auf der Mitte von + und −,
       liefe die Linie knapp darunter durch und sähe aus wie ein zweiter,
       dünner Bruchstrich. Brüche in Brüchen bleiben auf ihrer Mitte. */
    const aufLinie = k >= 0.95;
    const d = aufLinie ? Math.max(strichDicke(kk), 1.7) : strichDicke(kk);
    const achse = aufLinie ? -0.5 : M.achse * S * k;
    const ueberstand = 0.16 * u;
    const w = Math.max(oben.w, unten.w) + 2 * ueberstand;

    const luftOben = 0.24 * u + d / 2;
    const luftUnten = 0.3 * u + d / 2;
    // Mindesthöhe, damit a/b nicht anders sitzt als 1/2
    const nennerHoehe = Math.max(unten.h, M.xh * S * kk);
    const yOben = achse + luftOben + oben.t;
    const yUnten = achse - luftUnten - nennerHoehe;

    const teile = [
      verschiebe(oben, (w - oben.w) / 2, yOben),
      verschiebe(unten, (w - unten.w) / 2, yUnten),
    ];
    let box = vereine(teile);
    box.w = w;
    if (!knoten.ohneStrich) {
      const x0 = ueberstand * 0.25;
      const x1 = w - ueberstand * 0.25;
      box = mitStrichen(box, [
        aufLinie
          ? { p: [x0, achse, x1, achse], d, gerade: true }
          : { p: [x0, achse, (x0 + x1) / 2, achse + 0.015 * u, x1, achse], d },
      ]);
    }
    // etwas Luft links und rechts, damit der Strich nicht an Nachbarn klebt
    const rand = 0.12 * u;
    return { ...verschiebe(box, rand, 0), w: box.w + 2 * rand };
  }

  function wurzel(knoten, k) {
    const inhalt = setze(knoten.inhalt, k);
    const u = M.xh * S * k;
    const d = strichDicke(k);
    const oben = Math.max(inhalt.h, M.xh * S * k) + 0.32 * u;
    const unten = -Math.max(inhalt.t, 0) - 0.12 * u;
    const H = oben - unten;
    const hakenH = Math.min(H * 0.5, 0.85 * u);
    const hakenB = (0.52 + 0.06 * Math.min(H / u, 5)) * u;

    let index = null;
    let versatz = 0;
    if (knoten.index) {
      index = setze(knoten.index, Math.max(0.45, k * 0.55));
      const ziel = 0.32 * hakenB; // rechte Kante der Wurzelzahl
      versatz = Math.max(0, index.w - ziel);
    }

    const P = [
      versatz + 0.0, unten + hakenH * 0.7,
      versatz + 0.2 * hakenB, unten + hakenH,
      versatz + 0.52 * hakenB, unten,
      versatz + hakenB, oben,
      versatz + hakenB + inhalt.w + 0.18 * u, oben + 0.02 * u,
    ];
    const xInhalt = versatz + hakenB + 0.1 * u;
    let box = verschiebe(inhalt, xInhalt, 0);
    box.w = xInhalt + inhalt.w + 0.22 * u;
    box = mitStrichen(box, [{ p: P, d }]);
    if (index) {
      const ix = versatz + 0.32 * hakenB - index.w;
      const iy = unten + hakenH + 0.18 * u + index.t;
      box = vereine([box, verschiebe(index, Math.max(0, ix), iy)]);
      box.w = Math.max(box.w, xInhalt + inhalt.w + 0.22 * u);
    }
    const rand = 0.06 * u;
    return { ...verschiebe(box, rand, 0), w: box.w + 2 * rand };
  }

  function skript(knoten, k) {
    const basis = setze(knoten.basis, k);
    const u = M.xh * S * k;
    const ks = Math.max(0.45, k * 0.64);
    const teile = [basis];
    let breite = 0;
    let basisH = basis.w === 0 && !basis.glyphen.length ? M.xh * S * k : basis.h;
    // Hohe Buchstaben (b, d, f …) heben die Hochzahl nicht über die von
    // Ziffern — sonst stünde b² viel höher als a² daneben.
    if (basisH <= M.oberlaenge * S * k * 1.4) basisH = Math.min(basisH, M.ziffernHoehe * S * k);
    // nach einer Klammer etwas Luft, sonst klebt die Hochzahl am Bogen
    const x = basis.w + (knoten.basis.t === "klammer" ? 0.1 : 0.03) * u;
    if (knoten.hoch) {
      const hoch = setze(knoten.hoch, ks);
      const dy = Math.max(0.78 * u, basisH - 0.5 * Math.max(hoch.h, M.xh * S * ks * 1.3)) + hoch.t * 0.5;
      teile.push(verschiebe(hoch, x, dy));
      breite = Math.max(breite, hoch.w);
    }
    if (knoten.tief) {
      const tief = setze(knoten.tief, ks);
      const dy = -(0.32 * u + Math.max(0, basis.t - 0.25 * u) * 0.6);
      teile.push(verschiebe(tief, x, dy));
      breite = Math.max(breite, tief.w);
    }
    const box = vereine(teile);
    box.w = x + breite + 0.04 * u;
    return box;
  }

  /* Klammern um Brüche und Ähnliches werden hoch genug gezeichnet;
     um normale Ausdrücke bleiben es die Klammern der Schrift. */
  function klammer(knoten, k) {
    const inhalt = setze(knoten.inhalt, k);
    const u = M.xh * S * k;
    const hoch =
      inhalt.h > M.ziffernHoehe * S * k * 1.18 || inhalt.t > M.unterlaenge * S * k * 1.25;

    if (!hoch) {
      const teile = [];
      const auf = knoten.auf ? glyphBox(knoten.auf, k) : null;
      const zu = knoten.zu ? glyphBox(knoten.zu, k) : null;
      if (auf) teile.push({ box: auf });
      teile.push({ box: inhalt, abstand: auf ? 0.04 * u : 0 });
      if (zu) teile.push({ box: zu, abstand: 0.04 * u });
      return nebeneinander(teile);
    }

    const d = strichDicke(k);
    const oben = inhalt.h + 0.14 * u;
    const unten = -inhalt.t - 0.14 * u;
    const H = oben - unten;
    const mitte = (oben + unten) / 2;
    const breite = (0.26 + 0.05 * Math.min(H / u, 6)) * u;

    const form = (zeichen, links) => {
      // links = true: Klammer links vom Inhalt (öffnend)
      const spiegel = (x) => (links ? x : breite - x);
      switch (zeichen) {
        case "(":
        case ")": {
          // Die Form hängt am Zeichen, nicht an der Seite: ) ist immer rund nach rechts
          const s = (x) => (zeichen === "(" ? x : breite - x);
          const p = [];
          for (let i = 0; i <= 14; i++) {
            const t = (i / 14) * 2 - 1; // -1 oben … 1 unten
            p.push(s(breite * (0.12 + 0.83 * t * t)), mitte - t * (H / 2));
          }
          return p;
        }
        case "[":
        case "]": {
          const s = (x) => (zeichen === "[" ? x : breite - x);
          return [s(breite * 0.85), oben, s(breite * 0.2), oben, s(breite * 0.2), unten, s(breite * 0.85), unten];
        }
        case "{":
        case "}": {
          const s = (x) => (zeichen === "{" ? x : breite - x);
          return glaette([s(breite * 0.95), oben, s(breite * 0.5), oben - H * 0.08, s(breite * 0.48), mitte + H * 0.1, s(breite * 0.05), mitte, s(breite * 0.48), mitte - H * 0.1, s(breite * 0.5), unten + H * 0.08, s(breite * 0.95), unten]);
        }
        case "|":
          return [spiegel(breite * 0.5), oben, spiegel(breite * 0.5), unten];
        case "‖":
          return null;
        default:
          return null;
      }
    };

    const teile = [];
    const baueSeite = (zeichen, links) => {
      if (!zeichen) return null;
      const p = form(zeichen, links);
      // glatt: beim Verwackeln nur leicht drehen, die Punkte nicht einzeln
      // verschieben — bei so schmalen Bögen würde das zur Wellenlinie
      if (p) return mitStrichen({ w: breite, h: 0, t: 0, glyphen: [], striche: [] }, [{ p, d, glatt: true }]);
      return glyphBox(zeichen, k);
    };
    const auf = baueSeite(knoten.auf, true);
    const zu = baueSeite(knoten.zu, false);
    if (auf) teile.push({ box: auf });
    teile.push({ box: inhalt, abstand: auf ? 0.06 * u : 0 });
    if (zu) teile.push({ box: zu, abstand: 0.06 * u });
    return nebeneinander(teile);
  }

  function ueber(knoten, k) {
    const inhalt = setze(knoten.inhalt, k);
    const u = M.xh * S * k;
    const d = strichDicke(k);
    const y = Math.max(inhalt.h, M.ziffernHoehe * S * k) + 0.26 * u;
    const x0 = 0.04 * u;
    const x1 = Math.max(inhalt.w - 0.04 * u, x0 + 0.5 * u);
    const striche = [{ p: [x0, y, (x0 + x1) / 2, y + 0.02 * u, x1, y], d }];
    if (knoten.art === "pfeil") {
      striche.push({ p: [x1 - 0.26 * u, y + 0.2 * u, x1 + 0.02 * u, y, x1 - 0.26 * u, y - 0.2 * u], d });
    }
    const box = mitStrichen(inhalt, striche);
    box.w = Math.max(inhalt.w, x1 + 0.08 * u);
    return box;
  }

  function text(s, k) {
    const teile = [];
    for (const ch of Array.from(s)) {
      if (ch === " ") {
        teile.push({ box: { ...leer(), w: M.leer * S * k + laufweite(S * k) } });
        continue;
      }
      const b = glyphBox(ch, k);
      if (b) teile.push({ box: b });
      else {
        const ersatz = ersatzFuer(ch, k);
        if (ersatz) teile.push({ box: ersatz });
      }
    }
    return nebeneinander(teile);
  }

  /* Ein Zeichen, das die Schrift nicht hat: vielleicht kennen wir es. */
  const ERSATZ = { "π": "pi", "°": "deg", "·": "cdot", "×": "times", "∞": "infty", "µ": "mu", "μ": "mu", "Δ": "Delta" };
  function ersatzFuer(ch, k) {
    if (ch === "−" || ch === "–") return glyphBox("-", k);
    if (ERSATZ[ch]) return symbol(ERSATZ[ch], k);
    return null;
  }

  function setze(knoten, k) {
    if (!knoten) return leer();
    switch (knoten.t) {
      case "g": {
        const b = glyphBox(knoten.ch, k);
        return b || ersatzFuer(knoten.ch, k) || leer();
      }
      case "sym":
        return symbol(knoten.name, k);
      case "row":
        return zeile(knoten.items, k);
      case "bruch":
        return bruch(knoten, k);
      case "wurzel":
        return wurzel(knoten, k);
      case "skript":
        return skript(knoten, k);
      case "klammer":
        return klammer(knoten, k);
      case "ueber":
        return ueber(knoten, k);
      case "abstand":
        return { ...leer(), w: knoten.em * 2.2 * M.xh * S * k };
      case "text":
        return text(knoten.s, k);
      default:
        return leer();
    }
  }

  let box = setze(baum, 1);
  if (bruchzeile && (baum.kl === "bin" || baum.kl === "rel")) {
    box = verschiebe(box, 0, -M.achse * S);
  }
  // Kleiner Rand, damit eine Formel nicht am Nachbarwort klebt
  const rand = 0.05 * M.xh * S;
  return { ...verschiebe(box, rand, 0), w: box.w + 2 * rand };
}
